import isFunction from "lodash/isFunction";
import isEmpty from "lodash/isEmpty";
import isObject from "lodash/isObject";
import AnalyticsFactory from "./AnalyticsFactory";
import get from "lodash/get";
import keys from "lodash/keys";
import find from "lodash/find";
import includes from "lodash/includes";
import forEach from "lodash/forEach";
import map from "lodash/map";
import {
  forms,
  paths,
  PLAN_TYPE,
  ROOT_CHANNEL,
  WEB_CHANNEL
} from "utils/constants";
import head from "lodash/head";
import { emailRegex } from "validations";
import { getChannelFromUrl } from "utils/helpers";
import { GTM_PAYMENT_METHOD_NAME_MAPPING } from "constants/AnalyticsTypeConstants";
import EvamAnalytics from "./type/EvamAnalytics";

const FIELD_BLURRED = "field_blurred";
const FIELD_FOCUSED = "field_focused";
const FIELD_TOUCH = "field_touch";
const STOP_ASYNC_VALIDATION = "stop_async_validation";
const UPDATE_SYNC_ERRORS = "update_sync_errors";
const PUSH_PROFILE_EVENTS = [
  // "field_blurred",
  // "SelfActivationForm_submitted",
  "loggedInUserDetails_api_call_succeeded"
];
const BLUR_MONITORED_FIELDS = [
  "email",
  "contact_number",
  "identification_number",
  "port_number",
  "reConfirmidentificationNumber",
  "delivery_address_line_1",
  "first_name",
  "last_name",
  "contact_number"

];

const SET_SUBMIT = "submitted";
const SET_SUBMIT_SUCCEEDED_FORMS = [
  forms.PERSONAL_DETAILS_FORM,
  forms.DELIVERY_INFO_FORM,
  forms.PORT_IN_FORM,
  forms.NEW_NUMBER_FORM,
  forms.PRE_CHECKOUT_FORM,
  forms.SELF_ACTIVATION_FORM,
  forms.PHONES_FORM,
  forms.CONNECTION_DETAILS_FORM
];

const FIELD_VALUE_CHANGED = [
  "number_type",
  "sim_type",
  "telco",
  "payment_method",
  "nationality",
  "port_number",
  "storeId",
  "number_category",
  "e_sim_consent_disclaimer",
  "delivery_province",
  "delivery_city",
  "delivery_area",
  "delivery_slot_full_data",
  "delivery_slot",
  "email",
  "delivery_address_line_1",
  "dobYear",
  "dob",
  "age"
];

function getSuccessStatus(key, data) {
  switch (key) {
    case "paymentStatus":
    case "paymentReturn": {
      const statusValue = get(data, "payment_status", "");
      return statusValue.toLocaleLowerCase() === "success";
    }
    default: {
      return true;
    }
  }
}

const handleEvamStatusSuccess = (key, data, reduxStore) => {
  const event = `${key}_api_call_succeeded`;
  EvamAnalytics.pushEvent(event, data, "", reduxStore);
}

let reduxStore = {};

const getStoreDataForChargedEvent = orderParams => {
  const { activation_charge } = getStateData(
    reduxStore,
    "api.planService.data",
    {}
  );

  const formName = !isEmpty(
    getStateData(reduxStore, "form.DeliveryInfoForm.values", {})
  )
    ? forms.DELIVERY_INFO_FORM
    : !isEmpty(getStateData(reduxStore, "form.SelfActivationForm.values", {}))
    ? forms.SELF_ACTIVATION_FORM
    : forms.PERSONAL_DETAILS_FORM;

  const {
    delivery_slot_full_data,
    delivery_slot_fee,
    delivery_method
  } = getStateData(reduxStore, `form.${formName}.values`, {});

  return {
    ...orderParams,
    ...(isEmpty(get(orderParams, "order_summary", {})) && {
      order_summary: {
        onetime_total_fee: activation_charge + (delivery_slot_fee || 0)
      }
    }),
    ...(!isObject(get(orderParams, "delivery_slot")) && {
      delivery_slot: {
        delivery_method: get(delivery_method, "action"),
        delivery_slot_full_data
      }
    })
  };
};

/**
 * This is called saga file where saga fill invoke handleAnalytics on every action
 *
 * @param {Object} action
 */
export const handleAnalytics = action => {
  const analyticPreferences = getStateData(
    reduxStore,
    "api.analyticPreferences.data",
    {}
  );

  const defaultAnalytics = getStateData(
    reduxStore,
    "api.defaultAnalytics.data",
    {}
  );

  const { type } = action;
  const handler = analyticsConstructor[type]; // The handler is  used to determine whether or not the event should be pushed to analytics.
  if (handler && isFunction(handler)) {
    const eventData = handler(action, reduxStore); // If the handler is a function, then it will call that function with action and reduxStore as parameters.
    if (!isEmpty(eventData)) {
      const { event, data, message } = eventData;
      if (includes(PUSH_PROFILE_EVENTS, event)) {
        AnalyticsFactory.pushProfile(event, data, message, defaultAnalytics);
      }
      if (
        (!isEmpty(analyticPreferences) && !analyticPreferences.enabled) ||
        (isEmpty(analyticPreferences) &&
          !get(defaultAnalytics, "analyticDefaultPref.web"))
      ) {
        return;
      }
      AnalyticsFactory.pushEvent(event, data, message, reduxStore);
    }
  }
};

/**
 * handleAnalytics method will pick the correct action from the analyticsConstructor and returns event name and data object.
 *
 * @returns {{ event: String, data: Object }}
 */
const analyticsConstructor = {
  FETCH_REQUESTED: ({ payload = {} }) => {
    const { key, params = {} } = payload;
    return {
      event: `${key}_api_call_requested`,
      data: params
    };
  },
  FETCH_SUCCEEDED: ({ payload = {} }) => {
    const { key, data, isResendOtt } = payload;
    const planService = getStateData(reduxStore, "api.planService.data", {});
    const plan_name = planService?.name || getChannelFromUrl();
    if (!getSuccessStatus(key, data)){
      handleEvamStatusSuccess(key, data, reduxStore)
      return {};
    } 

    const userOrder =
      head(getStateData(reduxStore, "api.userOrders.data")) || {};
    let orderParamsData =
      getStateData(reduxStore, "api.order.params") ||
      getStateData(reduxStore, "api.selfActivationDeliveryOrder.params");
    let orderParams = !isEmpty(orderParamsData?.order)
      ? orderParamsData?.order
      : orderParamsData;
    orderParams = getStoreDataForChargedEvent(orderParams);
    const order = getStateData(reduxStore, "api.order.data");
    const referral_code = getStateData(
      reduxStore,
      "api.validPromoCode.data[0].code",
      ""
    );
    const referral_code_type = getStateData(
      reduxStore,
      "api.referralCode.data[0].code_type",
      ""
    );
    const forms = getStateData(reduxStore, "form", {});
    const currency = getStateData(
      reduxStore,
      "api.appGlobalSettings.data.currency",
      ""
    );
    const selectedAddons = getStateData(
      reduxStore,
      "api.selectedAddons.data",
      {}
    );
    const associatedProductVariants = getStateData(
      reduxStore,
      "api.planService.data.associatedProductVariants",
      []
    );
    const allUserSelectedAddons = map(
      selectedAddons,
      (selected, addon) => {
        if (selected) return addon;
      },
      []
    );
    const userSelectedAddons = [];
    forEach(
      allUserSelectedAddons,
      userSelectedAddon => {
        forEach(associatedProductVariants, associatedProductVariant => {
          if (get(associatedProductVariant, "sku") === userSelectedAddon)
            userSelectedAddons.push(associatedProductVariant);
        });
      },
      []
    );
    return {
      event: isResendOtt
        ? `resend_${key}_api_call_succeeded`
        : `${key}_api_call_succeeded`,
      data: {
        planService,
        plan_name,
        referral_code,
        referral_code_type,
        userOrder,
        forms,
        order,
        orderParams,
        currency,
        userSelectedAddons,
        ...data
      }
    };
  },
  FETCH_FAILED: ({ payload = {} }) => {
    const { key, error } = payload;
    return {
      event: `${key}_api_call_failed`,
      data: { error }
    };
  },
  "@@redux-form/FOCUS": ({ payload, meta = {} }) => {
    const { form, field } = meta;
    const formValues = getStateData(reduxStore, `form.${form}.values`, {});
    const formErrors = getStateData(reduxStore, `form.${form}.syncErrors`, {});

    return {
      event: FIELD_FOCUSED,
      data: { form, field, payload, formValues, formErrors }
    };
  },
  "@@redux-form/TOUCH": ({ payload, meta = {} }) => {
    const { form, field } = meta;
    const formValues = getStateData(reduxStore, `form.${form}.values`, {});
    const formErrors = getStateData(reduxStore, `form.${form}.syncErrors`, {});
    return {
      event: FIELD_TOUCH,
      data: { form, field, payload, formValues, formErrors }
    };
  },
  "@@redux-form/STOP_ASYNC_VALIDATION": ({ payload, meta = {} }) => {
    const { form, field } = meta;
    const formValues = getStateData(reduxStore, `form.${form}.values`, {});
    const formSyncErrors = getStateData(
      reduxStore,
      `form.${form}.syncErrors`,
      {}
    );
    return {
      event: STOP_ASYNC_VALIDATION,
      data: { form, field, payload, formValues, formSyncErrors }
    };
  },
  "@@redux-form/UPDATE_SYNC_ERRORS": ({ payload, meta = {} }) => {
    const { form } = meta;
    const formValues = getStateData(reduxStore, `form.${form}.values`, {});
    const formSyncErrors = getStateData(
      reduxStore,
      `form.${form}.syncErrors`,
      {}
    );
    return {
      event: UPDATE_SYNC_ERRORS,
      data: { form, payload, formValues, formSyncErrors }
    };
  },
  "@@redux-form/BLUR": ({ payload, meta = {} }) => {
    const { form, field } = meta;
    if (BLUR_MONITORED_FIELDS.includes(field) && payload) {
      const formValues = getStateData(reduxStore, `form.${form}.values`, {});
      const formSyncErrors = getStateData(
        reduxStore,
        `form.${form}.syncErrors`,
        {}
      );
      const loggedInUserEmail = getStateData(
        reduxStore,
        "api.loggedInUserDetails.data.email",
        ""
      );
      const referralCodeData = getStateData(
        reduxStore,
        "api.validPromoCode.data[0]",
        {}
      );

      if (loggedInUserEmail) {
        formValues["loggedInUserEmail"] = loggedInUserEmail;
      }

      if (!isEmpty(referralCodeData)) {
        formValues["referral_code_type"] = referralCodeData?.code_type || "";
        formValues["referral_code"] = referralCodeData?.code || "";
      }

      // making sure only valid emails are sent as events
      if (field === "email" && !isValidEmail(formValues.email)) {
        return;
      }

      formValues[field] = payload;
      return {
        event: FIELD_BLURRED,
        data: { form, field, payload, formValues, formSyncErrors }
      };
    }
  },
  "@@redux-form/SET_SUBMIT_SUCCEEDED": ({ type, meta = {} }) => {
    const { form } = meta;
    const formValues = getStateData(reduxStore, `form.${form}.values`, {});
    const plan_name =
      getStateData(reduxStore, "api.planService.data.title", "") ||
      getChannelFromUrl();
    const productCategory = getStateData(
      reduxStore,
      "api.planService.data.productCategory",
      ""
    );

    const selectedAddons = getStateData(
      reduxStore,
      "api.selectedAddons.data",
      {}
    );
    const associatedProductVariants = getStateData(
      reduxStore,
      "api.planService.data.associatedProductVariants",
      []
    );
    const allUserSelectedAddons = map(
      selectedAddons,
      (selected, addon) => {
        if (selected) return addon;
      },
      []
    );
    const userSelectedAddons = [];
    forEach(
      allUserSelectedAddons,
      userSelectedAddon => {
        forEach(associatedProductVariants, associatedProductVariant => {
          if (get(associatedProductVariant, "sku") === userSelectedAddon)
            userSelectedAddons.push(associatedProductVariant);
        });
      },
      []
    );

    if (SET_SUBMIT_SUCCEEDED_FORMS.includes(form)) {
      return {
        event: `${form}_${SET_SUBMIT}`,
        data: {
          form,
          formValues,
          plan_name,
          productCategory,
          userSelectedAddons
        }
      };
    }
  },
  "@@redux-form/CHANGE": ({ type, meta = {} }) => {
    const { form, field, payload } = meta;
    const formValues = getStateData(reduxStore, `form.${form}.values`, {});
    const formSyncErrors = getStateData(
      reduxStore,
      `form.${form}.syncErrors`,
      {}
    );
    if (FIELD_VALUE_CHANGED.includes(field)) {
      if (field === "storeId") {
        if (
          formValues.storeId &&
          formValues.storeId.length === 6 &&
          !localStorage.getItem("isStoreIdAdded")
        ) {
          localStorage.setItem("isStoreIdAdded", true);
          return {
            event: `${field}_entered`,
            data: { form, formValues, field, payload, formSyncErrors }
          };
        } else if (formValues.storeId && formValues.storeId.length <= 5) {
          localStorage.removeItem("isStoreIdAdded");
        }
      } else {
        return {
          event: `${field}_selected`,
          data: { form, formValues, field, payload, formSyncErrors }
        };
      }
    }
  },
  SET_DATA: ({ payload = {} }, reduxStore) => {
    const { key, data } = payload;
    const modalView = get(data, "type", "").toLowerCase();
    const isOptionAvailable = get(data, "isOptionAvailable", "N");
    const selectedKey = keys(data)[0];
    const addonSelected = get(data, selectedKey);
    const additionalProducts = getStateData(
      reduxStore,
      "api.suggestedAddons.data.productVariants"
    );
    const manageOrderAction =
      key === "guestOrder"
        ? {
            button_name: getStateData(
              reduxStore,
              `api.locale.data.manage_order_${data?.[selectedKey]}`,
              ""
            ),
            action_name: data?.[selectedKey]
          }
        : {};
    const addon = find(additionalProducts, { sku: selectedKey });
    const currentPath = window.location.pathname;

    const plan_name =
      getStateData(reduxStore, "api.planService.data.title", "") ||
      getChannelFromUrl();
    let orderParamsData =
      getStateData(reduxStore, "api.order.params") ||
      getStateData(reduxStore, "api.selfActivationDeliveryOrder.params");
    let orderParams = !isEmpty(orderParamsData?.order)
      ? orderParamsData?.order
      : orderParamsData;
    orderParams = getStoreDataForChargedEvent(orderParams);
    const order = getStateData(reduxStore, "api.order.data.order");
    const referral_code = getStateData(
      reduxStore,
      "api.validPromoCode.data[0].code",
      ""
    );
    let eventName;
    const productCategory = getStateData(
      reduxStore,
      "api.planService.data.productCategory",
      ""
    );
    if (
      currentPath ===
      get(paths, "PRE_CHECKOUT").replace(ROOT_CHANNEL, WEB_CHANNEL)
    ) {
      if (productCategory === get(PLAN_TYPE, "POSTPAID"))
        eventName = `${get(PLAN_TYPE, "POSTPAID")}_planService_requested`;
      if (productCategory === get(PLAN_TYPE, "PREPAID"))
        eventName = `${get(PLAN_TYPE, "PREPAID")}_planService_requested`;
    }

    if (addon) {
      const addonSelectionStatus = addonSelected ? "selected" : "deselected";
      eventName = `addon_${addonSelectionStatus}`;
    }

    if (modalView) {
      eventName = `${modalView}_requested`;
      if (
        modalView === "store_id_validate_popup" &&
        get(data, "isModalView") === true
      )
        eventName = "retail_icon_click";
      if (
        modalView === "store_id_validate_popup" &&
        get(data, "isModalView") === false
      )
        eventName = "store_id_popup_cancel_click";
      if (modalView === "store_id_deletion_popup")
        eventName = "store_id_delete_click";
    }

    const sim_type = getStateData(
      reduxStore,
      "form.ConnectionDetailsForm.values.sim_type"
    );
    const number_type = getStateData(
      reduxStore,
      "form.ConnectionDetailsForm.values.number_type"
    );
    const telco = getStateData(
      reduxStore,
      "form.ConnectionDetailsForm.values.telco"
    );
    const storeId = getStateData(
      reduxStore,
      "form.PersonalDetailsForm.values.storeId"
    );

    if (isOptionAvailable === "N" && key === "ga4Events") {
      eventName = "payment_type_selected";
      const paymentOption_name = get(data, "transaction_instrument_name", "");
      localStorage.setItem(
        "GA4Event_payment_type",
        GTM_PAYMENT_METHOD_NAME_MAPPING[paymentOption_name]
      );
    }
    if (key === "easypaisaLinking") {
      eventName = "link_your_easypaisa_click";
      localStorage.setItem(
        "GA4Event_payment_type",
        GTM_PAYMENT_METHOD_NAME_MAPPING["link_account_text"]
      );
    }
    if (key === "selectedNumberCategory") {
      eventName = "number_subcategory_selected";
    }

    if (key === "locationpicker") {
      eventName = "access_location_modal";
    }

    if (key === "locationconsentgiven") {
      eventName = "location_consent_given";
    }

    return {
      event: eventName ? eventName : `${key}_requested`,
      data: {
        plan_name,
        addon,
        self_activation: true,
        referral_code,
        selected: data?.[selectedKey],
        ...manageOrderAction,
        order,
        orderParams,
        sim_type,
        number_type,
        telco,
        productCategory,
        storeId
      }
    };
  }
};

export const currencyCode = () => {
  return getStateData(
    reduxStore,
    "api.appSettings.data.currency.currency_code"
  );
};

export const initPushNotification = store => {
  try {
    reduxStore = store;
    const stateAPI = getStateData(store, "api");
    const notificationDelay = get(
      stateAPI,
      "appSettings.data.plugins.clevertap.data.notifications.delay",
      3000
    );

    const translations = get(stateAPI, "locale.data", {});
    setTimeout(() => {
      if (!window.clevertapDisabled && window.clevertap) {
        window.clevertap.notifications.push({
          titleText: translations.clevertap_webPushTitleText,
          bodyText: translations.clevertap_webPushBodyText,
          okButtonText: translations.clevertap_webPushOkButtonText,
          rejectButtonText: translations.clevertap_webPushRejectButtonText,
          okButtonColor: "#f28046",
          hidePoweredByCT: true
        });
        console.log("CleverTap notification initialized");
      } else {
        console.log("CleverTap is undefined. Skipping push notification");
      }
    }, Number(notificationDelay));
  } catch (e) {
    console.error(
      "An error encountered during clevertap notifications initialization"
    );
    console.error(e);
  }
};

export const getStateData = (store, path, defaultValue) => {
  let state = {};
  if (isFunction(store?.getState)) {
    state = store.getState();
  }
  return get(state, path, defaultValue);
};

const isValidEmail = value => {
  return new RegExp(emailRegex, "i").test(value);
};
